import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Bookmark, Clock, CheckCircle, XCircle, Play, Pause, RotateCcw, Check, Plus, AlertTriangle, ArrowLeft, Home } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { getSubjectGradient } from '../utils/constants';
import { ArchiveQuestionModal } from './ArchiveQuestionModal';
interface QuestionOption {
  id: string;
  text: string;
}
interface Question {
  id: number;
  content: string;
  image?: string;
  options: QuestionOption[];
  correctOptionId: string;
  explanation?: string;
  videoUrl?: string;
}
interface QuestionSolveInterfaceProps {
  subjectId: string;
  subjectTitle: string;
  topic: string;
  questions: Question[];
  onFinish: (results: any) => void;
  onBack: () => void;
  startingQuestionIndex?: number;
  reviewMode?: boolean;
  initialSelectedOptions?: Record<number, string>;
  initialShowSolution?: Record<number, boolean>;
  initialArchivedQuestions?: number[];
  timeLimit?: number; // in minutes
  testTitle?: string;
  onConfirmFinish?: () => void;
}
export function QuestionSolveInterface({
  subjectId,
  subjectTitle,
  topic,
  questions,
  onFinish,
  onBack,
  startingQuestionIndex = 0,
  reviewMode = false,
  initialSelectedOptions = {},
  initialShowSolution = {},
  initialArchivedQuestions = [],
  timeLimit = 0,
  testTitle = '',
  onConfirmFinish
}: QuestionSolveInterfaceProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(startingQuestionIndex);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>(initialSelectedOptions);
  const [showSolution, setShowSolution] = useState<Record<number, boolean>>(reviewMode ? questions.reduce((acc, q) => ({
    ...acc,
    [q.id]: true
  }), {}) : initialShowSolution);
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(!reviewMode);
  const [archivedQuestions, setArchivedQuestions] = useState<number[]>(initialArchivedQuestions);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timeLimit * 60); // Convert minutes to seconds
  const [showConfirmFinish, setShowConfirmFinish] = useState(false);
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const hasSelectedOption = selectedOptions[currentQuestion.id] !== undefined;
  const isCorrect = hasSelectedOption && selectedOptions[currentQuestion.id] === currentQuestion.correctOptionId;
  const showingSolution = showSolution[currentQuestion.id] || false;
  // Gradient based on subject
  const gradient = getSubjectGradient(subjectId);
  // Function to get subject-specific placeholder image
  const getSubjectPlaceholder = (subjectId: string) => {
    switch (subjectId) {
      case 'matematik':
        return 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWF0aCUyMHByb2JsZW18ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      case 'turkce':
        return 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Ym9va3MlMjBhbmQlMjBwZW58ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      case 'fen':
        return 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c2NpZW5jZXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'inkılap':
        return 'https://images.unsplash.com/photo-1447069387593-a5de0862481e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aGlzdG9yeXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'ingilizce':
        return 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZW5nbGlzaHxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
      case 'din':
        return 'https://images.unsplash.com/photo-1585036156171-384164a8c675?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVsaWdpb258ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
      default:
        return 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZWR1Y2F0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60';
    }
  };
  // Timer logic for tracking time spent - stopwatch functionality
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerRunning && !reviewMode) {
      interval = setInterval(() => {
        setTimer(prevTimer => prevTimer + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, reviewMode]);
  // Countdown timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerRunning && !reviewMode && timeLimit > 0) {
      interval = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            // Time's up, finish the test
            clearInterval(interval as NodeJS.Timeout);
            handleFinishTest();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, reviewMode, timeLimit]);
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  // Format time as "X minutes Y seconds"
  const formatTimeLong = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) {
      return `${remainingSeconds} saniye`;
    } else if (remainingSeconds === 0) {
      return `${minutes} dakika`;
    } else {
      return `${minutes} dakika ${remainingSeconds} saniye`;
    }
  };
  // Handle option selection - Unified function for both main area and optical form
  const handleSelectOption = (questionId: number, optionId: string) => {
    if (reviewMode) return; // Prevent changing answer in review mode
    setSelectedOptions(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };
  // Handle checking the answer - only used in review mode
  const handleCheckAnswer = () => {
    setShowSolution(prev => ({
      ...prev,
      [currentQuestion.id]: true
    }));
  };
  // Navigate to next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  // Navigate to previous question
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  // Navigate to specific question
  const handleGoToQuestion = (index: number) => {
    if (index >= 0 && index < totalQuestions) {
      setCurrentQuestionIndex(index);
    }
  };
  // Toggle timer
  const handleToggleTimer = () => {
    if (!reviewMode) {
      setIsTimerRunning(!isTimerRunning);
    }
  };
  // Reset timer
  const handleResetTimer = () => {
    if (!reviewMode) {
      setTimer(0);
    }
  };
  // Open archive modal
  const handleOpenArchiveModal = () => {
    setShowArchiveModal(true);
  };
  // Handle archive question with additional data
  const handleArchiveQuestion = (data: {
    difficulty: 'Kolay' | 'Orta' | 'Zor';
    notes: string;
  }) => {
    // Get all questions from localStorage
    const savedQuestions = localStorage.getItem('questions');
    let allQuestions = savedQuestions ? JSON.parse(savedQuestions) : [];
    // Create a new question object for the archive if it doesn't exist
    const existingQuestion = allQuestions.find((q: any) => q.id === currentQuestion.id);
    if (!existingQuestion) {
      // Create a new archived question
      const archivedQuestion = {
        id: currentQuestion.id,
        subject: subjectTitle,
        topic: topic,
        subtopic: currentQuestion.content.substring(0, 60) + (currentQuestion.content.length > 60 ? '...' : ''),
        difficulty: data.difficulty,
        solved: selectedOptions[currentQuestion.id] === currentQuestion.correctOptionId,
        date: new Date().toISOString(),
        archived: true,
        content: currentQuestion.content,
        options: currentQuestion.options.map(o => o.text),
        correctOption: currentQuestion.options.findIndex(o => o.id === currentQuestion.correctOptionId),
        image: currentQuestion.image,
        likes: [],
        views: 0,
        notes: data.notes
      };
      // Add to questions array
      allQuestions.push(archivedQuestion);
    } else {
      // Update existing question with new data
      allQuestions = allQuestions.map((q: any) => q.id === currentQuestion.id ? {
        ...q,
        archived: true,
        difficulty: data.difficulty,
        notes: data.notes,
        // Update these fields to ensure they're current
        subject: subjectTitle,
        topic: topic,
        solved: selectedOptions[currentQuestion.id] === currentQuestion.correctOptionId
      } : q);
    }
    // Save updated questions back to localStorage
    localStorage.setItem('questions', JSON.stringify(allQuestions));
    // Update local state
    setArchivedQuestions([...archivedQuestions, currentQuestion.id]);
    // Close the modal
    setShowArchiveModal(false);
  };
  // Check if the current question is archived in localStorage
  useEffect(() => {
    const savedQuestions = localStorage.getItem('questions');
    if (savedQuestions) {
      const allQuestions = JSON.parse(savedQuestions);
      const isArchived = allQuestions.some((q: any) => q.id === currentQuestion.id && q.archived);
      if (isArchived && !archivedQuestions.includes(currentQuestion.id)) {
        setArchivedQuestions(prev => [...prev, currentQuestion.id]);
      } else if (!isArchived && archivedQuestions.includes(currentQuestion.id)) {
        setArchivedQuestions(prev => prev.filter(id => id !== currentQuestion.id));
      }
    }
  }, [currentQuestion.id]);
  // Finish the test
  const handleFinishTest = () => {
    // Stop the timer when test is finished
    setIsTimerRunning(false);
    // Prepare question results for the test results screen
    const questionResults = questions.map(question => {
      let status: 'correct' | 'incorrect' | 'unanswered' = 'unanswered';
      if (selectedOptions[question.id]) {
        status = selectedOptions[question.id] === question.correctOptionId ? 'correct' : 'incorrect';
      }
      return {
        id: question.id,
        status,
        content: question.content.length > 60 ? question.content.substring(0, 60) + '...' : question.content
      };
    });
    const results = {
      totalQuestions,
      answeredQuestions: Object.keys(selectedOptions).length,
      correctAnswers: Object.entries(selectedOptions).filter(([questionId, optionId]) => {
        const question = questions.find(q => q.id === parseInt(questionId));
        return question && optionId === question.correctOptionId;
      }).length,
      timeSpent: timer,
      timeSpentFormatted: formatTimeLong(timer),
      archivedQuestions,
      questionResults,
      selectedOptions // Pass the selected options to allow reviewing
    };
    // Call the onFinish callback to redirect to results screen
    onFinish(results);
  };
  // Option letter mapping
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
  // Get total answered questions
  const getAnsweredQuestionsCount = () => {
    return Object.keys(selectedOptions).length;
  };
  // Render confirm finish modal
  const renderConfirmFinishModal = () => {
    if (!showConfirmFinish) return null;
    return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-870">
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md" data-id="element-871">
          <div className="relative bg-black/90 rounded-xl p-6" data-id="element-872">
            <div className="flex flex-col items-center space-y-4 text-center" data-id="element-873">
              <AlertTriangle className="w-12 h-12 text-yellow-400" data-id="element-874" />
              <h3 className="text-xl font-bold" data-id="element-875">Testi Bitirmeyi Onayla</h3>
              <p className="text-gray-300" data-id="element-876">
                Testi bitirmek istediğinize emin misiniz? Bu işlem geri
                alınamaz.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 w-full mt-4" data-id="element-877">
                <button onClick={() => setShowConfirmFinish(false)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors" data-id="element-878">
                  İptal
                </button>
                <button onClick={() => {
                setShowConfirmFinish(false);
                handleFinishTest();
              }} className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-879">
                  Testi Bitir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="flex flex-col gap-4" data-id="element-880">
      {/* Revised Header Layout - Aurora-style Elements */}
      <div className="bg-black/60 p-4 rounded-lg flex items-center justify-between gap-3" data-id="element-881">
        {/* Left Side: Back button and Test Name */}
        <div className="flex items-center gap-3" data-id="element-882">
          <button onClick={onBack} className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 transition-colors" data-id="element-883">
            <ArrowLeft className="w-4 h-4 text-white" data-id="element-884" />
          </button>
          <h2 className="text-lg font-medium truncate" data-id="element-885">
            {testTitle || `${subjectTitle} - ${topic}`}
          </h2>
        </div>
        {/* Right Side: Grouped Elements (Question Counter, Timer, Archive Button) */}
        <div className="flex items-center gap-3" data-id="element-886">
          {/* Question Counter - Aurora-style Card */}
          <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600" data-id="element-887">
            <div className="bg-black/90 px-4 py-2 rounded-lg" data-id="element-888">
              <div className="text-lg font-bold text-white" data-id="element-889">
                Soru {currentQuestionIndex + 1} / {totalQuestions}
              </div>
            </div>
          </div>
          {/* Timer - Aurora-style Card */}
          <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-amber-500 to-orange-500" data-id="element-890">
            <div className="bg-black/90 px-3 py-2 rounded-lg flex items-center gap-2" data-id="element-891">
              <Clock className="w-5 h-5 text-amber-400" data-id="element-892" />
              <div className="text-lg font-semibold text-white" data-id="element-893">
                {timeLimit > 0 ? formatTime(timeLeft) : formatTime(timer)}
              </div>
              {!reviewMode && <button onClick={handleToggleTimer} className="p-1 rounded-md bg-white/5 hover:bg-white/10 transition-colors" data-id="element-894">
                  {isTimerRunning ? <Pause className="w-4 h-4" data-id="element-895" /> : <Play className="w-4 h-4" data-id="element-896" />}
                </button>}
            </div>
          </div>
          {/* Archive Button - Aurora-style Button */}
          {!reviewMode && <div className={`relative p-[1px] rounded-lg overflow-hidden ${archivedQuestions.includes(currentQuestion.id) ? 'bg-gradient-to-r from-purple-500 to-purple-700' : 'bg-gradient-to-r from-purple-600 to-indigo-600'}`} data-id="element-897">
              <button onClick={handleOpenArchiveModal} disabled={archivedQuestions.includes(currentQuestion.id)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors bg-black/90 ${archivedQuestions.includes(currentQuestion.id) ? 'text-purple-300' : 'text-white hover:bg-black/70'}`} data-id="element-898">
                {archivedQuestions.includes(currentQuestion.id) ? <Check className="w-5 h-5" data-id="element-899" /> : <Plus className="w-5 h-5" data-id="element-900" />}
                <span className="hidden sm:inline" data-id="element-901">
                  {archivedQuestions.includes(currentQuestion.id) ? 'Arşivlendi' : 'Arşive Ekle'}
                </span>
              </button>
            </div>}
        </div>
      </div>
      {/* Main Content - Two Column Layout */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-902">
        {/* Main Question Area (70-75%) */}
        <div className="md:w-3/4 space-y-6" data-id="element-903">
          {/* Question content */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-904">
            <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-905">
              <div className="prose prose-invert max-w-none" data-id="element-906">
                <p className="text-lg" data-id="element-907">{currentQuestion.content}</p>
              </div>
              {currentQuestion.image && <div className="mt-4" data-id="element-908">
                  <img src={currentQuestion.image} alt="Soru görseli" className="rounded-lg max-w-full max-h-96 mx-auto" onError={e => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = getSubjectPlaceholder(subjectId);
              }} data-id="element-909" />
                </div>}
            </div>
          </div>
          {/* Answer options */}
          <div className="space-y-4" data-id="element-910">
            {currentQuestion.options.map((option, index) => {
            const isSelected = selectedOptions[currentQuestion.id] === option.id;
            const isCorrectOption = option.id === currentQuestion.correctOptionId;
            const isWrongSelection = isSelected && !isCorrectOption && showingSolution;
            return <button key={option.id} onClick={() => handleSelectOption(currentQuestion.id, option.id)} disabled={reviewMode} className={`w-full p-4 rounded-lg text-left transition-colors flex items-center gap-4 
                  ${reviewMode ? isCorrectOption ? 'bg-green-500/20 text-green-400 border border-green-500' : isWrongSelection ? 'bg-red-500/20 text-red-400 border border-red-500' : 'bg-white/5 text-gray-300 border border-transparent' : isSelected ? 'bg-purple-500/20 text-purple-400 border border-purple-500' : 'bg-white/5 hover:bg-white/10 text-white border border-transparent'}`} data-id="element-911">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg font-medium 
                    ${reviewMode ? isCorrectOption ? 'bg-green-500/20 text-green-400 border border-green-500' : isWrongSelection ? 'bg-red-500/20 text-red-400 border border-red-500' : 'bg-black/60 text-gray-400 border border-gray-600' : isSelected ? 'bg-purple-500/20 text-purple-400 border border-purple-500' : 'bg-black/60 text-gray-400 border border-gray-600'}`} data-id="element-912">
                    {optionLetters[index]}
                  </div>
                  <span className="flex-1" data-id="element-913">{option.text}</span>
                  {reviewMode && <>
                      {isCorrectOption && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-914" />}
                      {isWrongSelection && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-915" />}
                    </>}
                </button>;
          })}
          </div>
          {/* Navigation buttons (mobile only) */}
          <div className="flex justify-between items-center md:hidden" data-id="element-916">
            <button onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${currentQuestionIndex === 0 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-917">
              <ChevronLeft className="w-5 h-5" data-id="element-918" />
              Önceki
            </button>
            <button onClick={handleNextQuestion} disabled={currentQuestionIndex === totalQuestions - 1} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${currentQuestionIndex === totalQuestions - 1 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-919">
              Sonraki
              <ChevronRight className="w-5 h-5" data-id="element-920" />
            </button>
          </div>
          {/* Finish test button (mobile only) */}
          <div className="md:hidden" data-id="element-921">
            {!reviewMode && <button onClick={() => setShowConfirmFinish(true)} className="w-full py-3 px-4 bg-gradient-to-r from-red-600 to-rose-600 rounded-lg text-white font-medium hover:opacity-90 transition-colors" data-id="element-922">
                Testi Bitir
              </button>}
          </div>
        </div>
        {/* Right Control Panel (25-30%) */}
        <div className="md:w-1/4" data-id="element-923">
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 sticky top-24" data-id="element-924">
            <div className="relative p-4 bg-black/90 rounded-xl space-y-4" data-id="element-925">
              {/* Navigation buttons */}
              <div className="flex justify-between items-center gap-2" data-id="element-926">
                <button onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0} className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors 
                  ${currentQuestionIndex === 0 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-927">
                  <ChevronLeft className="w-4 h-4" data-id="element-928" />
                  Önceki
                </button>
                <button onClick={handleNextQuestion} disabled={currentQuestionIndex === totalQuestions - 1} className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors 
                  ${currentQuestionIndex === totalQuestions - 1 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-929">
                  Sonraki
                  <ChevronRight className="w-4 h-4" data-id="element-930" />
                </button>
              </div>
              {/* Optical Form */}
              <div className="mt-4 border-t border-gray-800 pt-4" data-id="element-931">
                <h3 className="text-sm font-medium mb-3 text-gray-400" data-id="element-932">
                  Optik Form
                </h3>
                <div className="max-h-[400px] overflow-y-auto pr-2 space-y-2" data-id="element-933">
                  {questions.map((question, index) => {
                  const selectedOption = selectedOptions[question.id];
                  const isCurrentQuestion = index === currentQuestionIndex;
                  return <div key={question.id} className={`flex items-center py-2 border-b border-gray-800/30 ${isCurrentQuestion ? 'bg-purple-900/20 rounded-lg px-2' : ''}`} data-id="element-934">
                        <div className={`w-8 font-medium text-center cursor-pointer ${isCurrentQuestion ? 'text-purple-400' : ''}`} onClick={() => handleGoToQuestion(index)} data-id="element-935">
                          {index + 1}
                        </div>
                        <div className="flex-1 flex items-center justify-between gap-1" data-id="element-936">
                          {['A', 'B', 'C', 'D'].map(optionId => <button key={optionId} onClick={() => handleSelectOption(question.id, optionId)} disabled={reviewMode} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs 
                                ${selectedOptions[question.id] === optionId ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400' : 'border border-gray-600 text-gray-400 hover:border-gray-400'}`} data-id="element-937">
                              {optionId}
                            </button>)}
                        </div>
                      </div>;
                })}
                </div>
              </div>
              {/* Stats */}
              <div className="pt-4 border-t border-gray-800 mt-4" data-id="element-938">
                <div className="space-y-2" data-id="element-939">
                  <div className="flex justify-between items-center" data-id="element-940">
                    <span className="text-gray-400" data-id="element-941">Toplam Soru</span>
                    <span className="font-medium" data-id="element-942">{totalQuestions}</span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-943">
                    <span className="text-gray-400" data-id="element-944">Cevaplanan</span>
                    <span className="font-medium text-purple-400" data-id="element-945">
                      {getAnsweredQuestionsCount()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-946">
                    <span className="text-gray-400" data-id="element-947">Boş</span>
                    <span className="font-medium" data-id="element-948">
                      {totalQuestions - getAnsweredQuestionsCount()}
                    </span>
                  </div>
                </div>
              </div>
              {/* Finish Test Button */}
              {!reviewMode && <div className="pt-4 border-t border-gray-800" data-id="element-949">
                  <button onClick={() => setShowConfirmFinish(true)} className="w-full py-3 px-4 bg-gradient-to-r from-red-600 to-rose-600 rounded-lg text-white font-medium hover:opacity-90 transition-colors" data-id="element-950">
                    Testi Bitir
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Archive Question Modal */}
      {showArchiveModal && <ArchiveQuestionModal onClose={() => setShowArchiveModal(false)} onSave={handleArchiveQuestion} question={currentQuestion} data-id="element-951" />}
      {/* Confirm Finish Modal */}
      {renderConfirmFinishModal()}
    </div>;
}